import { useMemo, useState } from "react"
import type { SlotInfo } from "@/lib/api/appointments"
import { cx } from "@/lib/utils/cx"

// The one shared slot picker. Day pills → a grid of times. Three separate
// slot UIs existed (calendar week table, public book page, reschedule grid)
// and none was shareable; this one is over `SlotInfo[]` so the calendar page
// and the public link can adopt it too.

function dayKeyOf(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

interface SlotPickerProps {
  slots: SlotInfo[]
  value: string | null
  onChange: (startsAt: string | null) => void
  /** Show at most this many days. */
  days?: number
  compact?: boolean
}

export function SlotPicker({ slots, value, onChange, days: maxDays = 8, compact }: SlotPickerProps) {
  const [dayIdx, setDayIdx] = useState(0)

  const days = useMemo(() => {
    const map = new Map<string, { date: Date; slots: SlotInfo[] }>()
    for (const s of slots) {
      if (s.free <= 0) continue
      const k = dayKeyOf(s.startsAt)
      const entry = map.get(k) ?? { date: new Date(s.startsAt), slots: [] }
      entry.slots.push(s)
      map.set(k, entry)
    }
    return [...map.values()]
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, maxDays)
  }, [slots, maxDays])

  const day = days[Math.min(dayIdx, Math.max(days.length - 1, 0))]

  if (days.length === 0) {
    return (
      <p className="text-[12.5px] text-zinc-500 py-3">
        Nothing open in the next two weeks — check the calendar's working hours.
      </p>
    )
  }

  return (
    <div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {days.map((d, i) => (
          <button
            key={d.date.toISOString()}
            type="button"
            onClick={() => {
              setDayIdx(i)
              onChange(null)
            }}
            className={cx(
              "shrink-0 rounded-lg px-3 py-1.5 text-center transition-colors duration-150 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20 focus-visible:ring-offset-1 motion-reduce:transition-none",
              i === dayIdx
                ? "bg-zinc-900 text-white"
                : "bg-white border border-zinc-200 text-zinc-700 hover:border-zinc-300",
            )}
          >
            <span className="block text-[10px] font-semibold uppercase opacity-70">
              {d.date.toLocaleDateString("en-US", { weekday: "short" })}
            </span>
            <span className="block text-[13.5px] font-bold tabular-nums leading-tight">
              {d.date.getDate()}
            </span>
          </button>
        ))}
      </div>

      <div className={cx("mt-2.5 grid gap-1.5", compact ? "grid-cols-4" : "grid-cols-3")}>
        {(day?.slots ?? []).map((s) => (
          <button
            key={s.startsAt}
            type="button"
            onClick={() => onChange(s.startsAt)}
            title={s.free < s.capacity ? `${s.free} of ${s.capacity} open` : undefined}
            className={cx(
              "h-10 rounded-lg text-[13px] font-semibold tabular-nums transition-colors duration-150 ease-out-quart active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/30 focus-visible:ring-offset-1 motion-reduce:transition-none",
              value === s.startsAt
                ? "bg-emerald-600 text-white"
                : "bg-white border border-zinc-200 text-zinc-700 hover:border-emerald-300",
            )}
          >
            {new Date(s.startsAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
          </button>
        ))}
      </div>
    </div>
  )
}
